import React, { useState, useEffect } from "react";

const WalletProfileSection = ({ userToken }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [creatingWallet, setCreatingWallet] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  // Helper function to transform getwallets API data to our expected format
  const transformWalletsData = (walletsArray) => {
    const grouped = {};

    walletsArray.forEach((wallet) => {
      const currency = wallet.currency;
      if (!grouped[currency]) {
        grouped[currency] = {
          currency: currency,
          currency_name: currency, // We'll use the currency code as name
          addresses: [],
        };
      }

      const networkDisplay =
        wallet.blockchain_currency !== currency
          ? wallet.blockchain_currency === "ETH"
            ? "ERC-20"
            : wallet.blockchain_currency === "BNB"
            ? "BEP-20"
            : wallet.blockchain_currency === "TRX"
            ? "TRC-20"
            : wallet.blockchain_currency
          : null;

      grouped[currency].addresses.push({
        network: wallet.blockchain_currency,
        network_name: wallet.blockchain_currency,
        network_display: networkDisplay,
        address: wallet.address,
        is_blocked: wallet.is_blocked || false,
        created: new Date().toISOString(), // Fallback date
        qr_data: wallet.address,
      });
    });

    return Object.values(grouped);
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);

      if (!userToken) {
        throw new Error("No authentication token found. Please login again.");
      }

      // Determine the backend URL
      const backendUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      console.log("Using backend URL:", backendUrl);

      // Try the enhanced profile API first, fallback to wallet-specific API
      let response = await fetch(`${backendUrl}/v1/profile/`, {
        headers: {
          Authorization: `Token ${userToken}`,
          "Content-Type": "application/json",
        },
      });

      // If profile API doesn't work, try the dedicated wallet API
      if (
        !response.ok ||
        response.headers.get("content-type")?.includes("text/html")
      ) {
        console.log("Profile API not available, trying wallet-profile API...");
        response = await fetch(`${backendUrl}/v1/wallet-profile/`, {
          headers: {
            Authorization: `Token ${userToken}`,
            "Content-Type": "application/json",
          },
        });

        // If that doesn't work either, try the existing getwallets API
        if (
          !response.ok ||
          response.headers.get("content-type")?.includes("text/html")
        ) {
          console.log(
            "Wallet-profile API not available, trying existing getwallets API..."
          );
          const getWalletsResponse = await fetch(
            `${backendUrl}/v1/getwallets/`,
            {
              method: "POST",
              headers: {
                Authorization: `Token ${userToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({}), // Empty body as per the original API
            }
          );

          if (
            getWalletsResponse.ok &&
            getWalletsResponse.headers
              .get("content-type")
              ?.includes("application/json")
          ) {
            const walletsData = await getWalletsResponse.json();
            // Transform the data to match our expected format
            setProfile({
              wallet_addresses: {
                total_wallets: walletsData.length,
                active_wallets: walletsData.filter((w) => !w.is_blocked).length,
                currencies: transformWalletsData(walletsData),
                raw_addresses: walletsData,
              },
              user: { username: "Current User" },
            });
            return;
          }

          // If all APIs fail, throw a comprehensive error
          response = getWalletsResponse;
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API Error (${response.status}): ${errorText.substring(0, 100)}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error(
          "API returned HTML instead of JSON. Check if backend is running and URLs are correct."
        );
      }

      const data = await response.json();

      // Handle different API response formats
      if (data.wallet_addresses) {
        // This is from the profile API
        setProfile(data);
      } else if (data.currencies) {
        // This is from the wallet-profile API
        setProfile({
          wallet_addresses: data,
          user: { username: "Current User" }, // Placeholder
        });
      } else {
        throw new Error("Unexpected API response format");
      }
    } catch (err) {
      console.error("Wallet profile fetch error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (address, currency) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(`${currency}-${address}`);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const createNewAddress = async (currency, network = null) => {
    try {
      setCreatingWallet(`${currency}-${network || currency}`);

      if (!userToken) {
        throw new Error("No authentication token found. Please login again.");
      }

      const requestData = { currency };
      if (network && network !== currency) {
        requestData.blockchain_currency = network;
      }

      const backendUrl =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8000/api";
      const response = await fetch(`${backendUrl}/v1/wallet-profile/create/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${userToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to create wallet (${response.status}): ${errorText.substring(
            0,
            100
          )}`
        );
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error(
          "Server returned HTML instead of JSON. Check if backend APIs are properly configured."
        );
      }

      const data = await response.json();

      // Refresh profile to show new address
      await fetchProfile();

      // Show success message (you can customize this)
      alert(`New ${currency} address created successfully!`);
    } catch (err) {
      console.error("Error creating address:", err);
      alert(`Failed to create ${currency} address: ${err.message}`);
    } finally {
      setCreatingWallet(null);
    }
  };

  // Get logo path for cryptocurrency symbols
  const getLogoPath = (symbol) => {
    const logoMap = {
      'BTC': '/btc-logo.svg',
      'BTC1': '/btc1-logo.svg',
      'ETH': '/eth-logo.svg',
      'TRX': '/trx-logo.svg',
      'USDT': '/usdt.svg',
      'BNB': '/bnb.svg',
      'MATIC': '/matic.svg',
      "FTT": "/ftt-logo.svg",
      "OPTMISIM": "/optimism.svg"
    };
    return logoMap[symbol] || null;
  };

  const getNetworkIcon = (currency, network) => {
    const icons = {
      BTC: "₿",
      ETH: "Ξ",
      USDT: "₮",
      BNB: "🔸",
      TRX: "◈",
      MATIC: "🔷",
    };
    return icons[currency] || icons[network] || "💰";
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="text-red-800">
          <h3 className="font-semibold">Error loading wallet addresses</h3>
          <p className="text-sm mt-1">{error}</p>

          {/* Debug Information */}
          <div className="mt-3 p-3 bg-red-100 rounded text-xs">
            <p>
              <strong>Debug Info:</strong>
            </p>
            <p>Token available: {userToken ? "Yes" : "No"}</p>
            <p>
              Token preview:{" "}
              {userToken ? `${userToken.substring(0, 10)}...` : "None"}
            </p>
            <p>Current URL: {window.location.href}</p>
          </div>

          <div className="flex gap-2 mt-3">
            <button
              onClick={fetchProfile}
              className="text-sm text-red-600 hover:text-red-800 underline"
            >
              Try again
            </button>
            <button
              onClick={() => {
                console.log("Debug info:", {
                  userToken: userToken
                    ? `${userToken.substring(0, 20)}...`
                    : null,
                  localStorage: Object.keys(localStorage),
                  currentURL: window.location.href,
                });
                alert(
                  "Debug info logged to console. Check browser developer tools."
                );
              }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Debug Info
            </button>
          </div>
        </div>
      </div>
    );
  }

  const walletData = profile?.wallet_addresses;

  if (!walletData || walletData.total_wallets === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-white mb-4">
        My Wallet Addresses
        </h2>
        <div className="text-center py-8">
          <div className="text-white mb-4 text-5xl"></div>
          <p className="text-white mb-4">No wallet addresses found</p>
          <button
            onClick={() => createNewAddress("BTC1")}
            className={"w-full px-6 py-2 !text-white rounded-md font-black transition-colors shadow-md bg-[#0E1696] hover:bg-[#0E1696]"
            }
          >
            
            + Create Your First Wallet
          </button>
        </div>
      </div>
    );
  }

  // Find BTC1 currency and other currencies
  const btc1Currency = walletData.currencies.find((c) => c.currency === "BTC1");
  const otherCurrencies = walletData.currencies.filter(
    (c) => c.currency !== "BTC1"
  );
  const btc1Address = btc1Currency?.addresses?.[0];

  return (
    <div className="bg-white p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 !bg-black rounded-lg flex items-center justify-center text-white font-bold text-lg">
            💰
          </div>
          <div>
            <h2 className="text-xl font-bold ">
              Wallet Addresses
            </h2>
            <p className="text-sm text-white">
              {walletData.active_wallets} active • {walletData.total_wallets}{" "}
              total
            </p>
          </div>
        </div>
        <button
          onClick={fetchProfile}
          className="p-2 text-white hover:text-white hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          🔄
        </button>
      </div>

      {/* Main BTC1 Profile Address */}
      {btc1Currency && btc1Address && (
        <div className="bg-gray-900 border-2 border-gray-200 hover:border-[#2A356D] rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3 mb-3">
            {getLogoPath("BTC1") ? (
              <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                <img 
                  src={getLogoPath("BTC1")} 
                  alt="BTC1"
                  className="w-full h-full object-contain" 
                />
              </div>
            ) : (
              <div className="w-8 h-8 bg-[#0E1696] rounded-full flex items-center justify-center text-white font-bold">
                ₿
              </div>
            )}
            <div>
              <h3 className="font-bold text-white">BTC1 Profile Address</h3>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-xs text-white">Primary Wallet</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-3 border border-[#2A356D]">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0 mr-3">
                <div className="font-mono text-sm  break-all">
                  {btc1Address.address}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => copyToClipboard(btc1Address.address, "BTC1")}
                  className={`p-2 rounded-lg text-sm transition-all ${
                    copiedAddress === `BTC1-${btc1Address.address}`
                      ? "bg-green-500 text-white"
                      : " text-white"
                  }`}
                  title={
                    copiedAddress === `BTC1-${btc1Address.address}`
                      ? "Copied!"
                      : "Copy"
                  }
                >
                  {copiedAddress === `BTC1-${btc1Address.address}` ? "✓" : "📋"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Other Currencies - Compact Grid */}
      {otherCurrencies.length > 0 && (
        <div>
          <h3 className="font-semibold  mb-3">Other Currencies</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {otherCurrencies.map((currency) => (
              <div
                key={currency.currency}
                className="border border-gray-200 rounded-lg p-4 hover:border-[#2A356D] transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getLogoPath(currency.currency) ? (
                      <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                        <img 
                          src={getLogoPath(currency.currency)} 
                          alt={currency.currency}
                          className="w-full h-full object-contain" 
                        />
                      </div>
                    ) : (
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                          currency.currency === "USDT"
                            ? "bg-green-500"
                            : currency.currency === "ETH"
                            ? "bg-blue-500"
                            : currency.currency === "BNB"
                            ? "bg-[#0E1696]"
                            : currency.currency === "TRX"
                            ? "bg-red-500"
                            : "bg-gray-500"
                        }`}
                      >
                        {getNetworkIcon(currency.currency)}
                      </div>
                    )}
                    <div>
                      <h4 className="font-medium  text-sm">
                        {currency.currency}
                      </h4>
                      <p className="text-xs text-white">
                        {currency.addresses.length} address
                        {currency.addresses.length !== 1 ? "es" : ""}
                      </p>
                    </div>
                  </div>

                  {currency.currency === "USDT" && (
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          createNewAddress(currency.currency, e.target.value);
                          e.target.value = "";
                        }
                      }}
                      className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                      disabled={creatingWallet?.startsWith(currency.currency)}
                    >
                      <option value="">+ Add</option>
                      <option value="ETH">ERC-20</option>
                      <option value="BNB">BEP-20</option>
                      <option value="TRX">TRC-20</option>
                    </select>
                  )}
                </div>

                <div className="space-y-2">
                  {currency.addresses.map((address) => {
                    const addressKey = `${currency.currency}-${address.network}-${address.address}`;
                    const isCopied =
                      copiedAddress ===
                      `${currency.currency}-${address.address}`;

                    return (
                      <div
                        key={addressKey}
                        className="bg-gray-50 rounded-lg p-3"
                      >
                        {address.network_display && (
                          <div className="flex items-center gap-1 mb-1">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                address.network === "ETH"
                                  ? "bg-blue-100 text-blue-700"
                                  : address.network === "BNB"
                                  ? "bg-yellow-100 text-white"
                                  : address.network === "TRX"
                                  ? "bg-red-100 text-red-700"
                                  : "bg-gray-100 text-white"
                              }`}
                            >
                              {address.network_display}
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 mr-2">
                            <div className="font-mono text-xs text-white break-all">
                              {address.address}
                            </div>
                          </div>

                          {!address.is_blocked && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    address.address,
                                    currency.currency
                                  )
                                }
                                className={`p-1.5 rounded text-xs transition-all ${
                                  isCopied
                                    ? "bg-green-500 text-white"
                                    : "bg-gray-700 hover:bg-gray-800 text-white"
                                }`}
                                title={isCopied ? "Copied!" : "Copy"}
                              >
                                {isCopied ? "✓" : "📋"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletProfileSection;
